import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Calendar,
  Eye,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Search,
  X,
  Mail,
  Phone,
  MapPin,
  Building2,
  BookOpen,
  ChevronRight,
  Twitter,
  Linkedin,
  Github,
  Globe,
  Sparkles,
  Flame,
  TrendingUp,
  Grid3x3,
  List,
  Filter,
  ChevronDown,
  Award,
  Clock,
  Heart,
  MessageCircle,
  Share2,
  Briefcase,
  GraduationCap,
  Languages,
} from "lucide-react";
import { blogService } from "../../api/services/blogService";
import { adminService } from "../../api/services/adminService";
import { format } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";

// Blog Image Component for BLOB handling
const BlogImage = ({ blog, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (blog?.featuredImageBlob) {
      setImageUrl(blog.featuredImageBlob);
      setImageLoading(false);
    } else if (blog?.featuredImage) {
      setImageUrl(blog.featuredImage);
      setImageLoading(false);
    } else {
      setImageLoading(false);
    }
  }, [blog]);

  if (imageLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={24} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={blog?.title}
        className={`object-cover ${className}`}
      />
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <BookOpen className="w-12 h-12 text-gray-400" />
    </div>
  );
};

// Author Profile Card Component
const AuthorProfileCard = ({ author, stats }) => {
  const authorName =
    author?.firstName && author?.lastName
      ? `${author.firstName} ${author.lastName}`
      : author?.name || "Anonymous";
  const authorInitials = authorName.charAt(0);
  const roleDisplay = author?.role?.replace("_", " ") || "Author";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden"
    >
      {/* Decorative Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-secondary-500/5 to-transparent rounded-3xl" />

      <div className="relative bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/30 dark:border-gray-800/30">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center lg:items-start">
            <div className="relative">
              <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-2xl">
                <span className="text-4xl font-bold text-white">
                  {authorInitials}
                </span>
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-green-500 border-4 border-white dark:border-gray-900 shadow-lg" />
            </div>

            {/* Social Links */}
            <div className="flex gap-2 mt-4">
              {author?.twitter && (
                <a
                  href={author.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/50 dark:bg-gray-800/50 hover:bg-primary-500 hover:text-white transition-all"
                >
                  <Twitter className="w-4 h-4" />
                </a>
              )}
              {author?.linkedin && (
                <a
                  href={author.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/50 dark:bg-gray-800/50 hover:bg-primary-500 hover:text-white transition-all"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {author?.github && (
                <a
                  href={author.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/50 dark:bg-gray-800/50 hover:bg-primary-500 hover:text-white transition-all"
                >
                  <Github className="w-4 h-4" />
                </a>
              )}
              {author?.website && (
                <a
                  href={author.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/50 dark:bg-gray-800/50 hover:bg-primary-500 hover:text-white transition-all"
                >
                  <Globe className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Author Info Section */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                {authorName}
              </h1>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium">
                <Award className="w-3 h-3" />
                {roleDisplay}
              </span>
            </div>

            {author?.title && (
              <p className="text-gray-600 dark:text-gray-400 mb-4 flex items-center justify-center lg:justify-start gap-2">
                <Briefcase className="w-4 h-4" />
                {author.title}
              </p>
            )}

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-4">
              {author?.email && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Mail className="w-4 h-4" />
                  <span>{author.email}</span>
                </div>
              )}
              {author?.phoneNumber && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Phone className="w-4 h-4" />
                  <span>{author.phoneNumber}</span>
                </div>
              )}
              {author?.location && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="w-4 h-4" />
                  <span>{author.location}</span>
                </div>
              )}
              {author?.company && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Building2 className="w-4 h-4" />
                  <span>{author.company}</span>
                </div>
              )}
            </div>

            {author?.bio && (
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {author.bio}
              </p>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              <div className="text-center p-3 rounded-xl bg-white/50 dark:bg-gray-800/50">
                <div className="text-2xl font-bold text-primary-600">
                  {stats.postCount}
                </div>
                <div className="text-xs text-gray-500">Articles</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-white/50 dark:bg-gray-800/50">
                <div className="text-2xl font-bold text-primary-600">
                  {stats.totalViews.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Total Views</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-white/50 dark:bg-gray-800/50">
                <div className="text-2xl font-bold text-primary-600">
                  {stats.totalLikes.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Total Likes</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-white/50 dark:bg-gray-800/50">
                <div className="text-2xl font-bold text-primary-600">
                  {stats.totalComments.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Comments</div>
              </div>
            </div>

            {/* Expertise Tags */}
            {author?.expertise && author.expertise.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4 justify-center lg:justify-start">
                {author.expertise.map((skill, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-400"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Blog Card Component
const BlogCard = ({ blog, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all duration-300"
    >
      <Link to={`/blog/${blog.slug}`} className="block">
        <div className="aspect-[16/11] overflow-hidden bg-gradient-to-br from-primary-500/10 to-secondary-500/10">
          <BlogImage
            blog={blog}
            className="w-full h-full group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>
                {format(
                  new Date(blog.publishedAt || blog.createdAt),
                  "MMM dd, yyyy",
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{blog.readingTime || 5} min read</span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{blog.views || 0}</span>
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
            {blog.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-4">
            {blog.excerpt ||
              blog.content?.substring(0, 100)?.replace(/<[^>]*>/g, "")}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                <User className="w-3 h-3 text-white" />
              </div>
              <span className="text-xs text-gray-500">
                {blog.author || "Anonymous"}
              </span>
            </div>
            <span className="text-primary-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              Read More <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

// Blog List Item Component
const BlogListItem = ({ blog, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all duration-300"
    >
      <div className="flex flex-col md:flex-row">
        <Link to={`/blog/${blog.slug}`} className="md:w-72 overflow-hidden">
          <div className="aspect-[16/10] md:aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary-500/10 to-secondary-500/10">
            <BlogImage
              blog={blog}
              className="w-full h-full group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        </Link>
        <div className="flex-1 p-6">
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 flex-wrap">
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {format(
                  new Date(blog.publishedAt || blog.createdAt),
                  "MMM dd, yyyy",
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{blog.readingTime || 5} min read</span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              <span>{blog.views || 0} views</span>
            </div>
          </div>
          <Link to={`/blog/${blog.slug}`}>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
              {blog.title}
            </h3>
          </Link>
          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {blog.excerpt ||
              blog.content?.substring(0, 180)?.replace(/<[^>]*>/g, "")}
          </p>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {blog.author || "Anonymous"}
              </span>
            </div>
            <Link
              to={`/blog/${blog.slug}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/10 hover:bg-primary-500 text-primary-600 hover:text-white rounded-xl transition-all duration-300 text-sm font-medium"
            >
              Read Article
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const BlogAuthor = () => {
  const { authorId } = useParams();
  const [author, setAuthor] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("latest");
  const [showFilters, setShowFilters] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();
  const [stats, setStats] = useState({
    postCount: 0,
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
  });
  const blogsPerPage = 9;

  useEffect(() => {
    if (authorId && !isNaN(parseInt(authorId))) {
      fetchAuthorAndBlogs();
    } else {
      setError("Invalid author ID");
      setLoading(false);
    }
  }, [authorId, currentPage, sortBy]);

  const fetchAuthorAndBlogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const blogsRes = await blogService.getBlogsByAuthor(authorId, {
        page: currentPage,
        limit: blogsPerPage,
        sortBy:
          sortBy === "latest"
            ? "publishedAt"
            : sortBy === "popular"
              ? "views"
              : "publishedAt",
        sortOrder: "DESC",
      });

      let blogsData =
        blogsRes?.data?.blogs || blogsRes?.blogs || blogsRes?.data || [];

      if (sortBy === "trending") {
        blogsData = blogsData.sort((a, b) => (b.views || 0) - (a.views || 0));
      }

      setBlogs(blogsData);
      const total =
        blogsRes?.data?.pagination?.total ||
        blogsRes?.pagination?.total ||
        blogsData.length;
      setTotalPages(Math.ceil(total / blogsPerPage));

      // Calculate stats
      const totalViews = blogsData.reduce((sum, b) => sum + (b.views || 0), 0);
      const totalLikes = blogsData.reduce((sum, b) => sum + (b.likes || 0), 0);
      const totalComments = blogsData.reduce(
        (sum, b) => sum + (b.commentsCount || 0),
        0,
      );

      setStats({
        postCount: blogsData.length,
        totalViews,
        totalLikes,
        totalComments,
      });

      // Set author info from the first blog
      if (blogsData.length > 0) {
        const firstBlog = blogsData[0];
        setAuthor({
          firstName: firstBlog.authorName || firstBlog.author,
          name: firstBlog.author || "Anonymous",
          bio: firstBlog.authorBio || "",
          email: firstBlog.authorEmail || "",
          role: "author",
        });
      } else {
        // If no blogs, try to get author info from admin service
        try {
          const authorRes = await adminService.getAdminByUuid(authorId);
          const authorData =
            authorRes?.data?.admin || authorRes?.admin || authorRes?.data;
          setAuthor(authorData);
        } catch (err) {
          // If admin not found, use default
          setAuthor({
            firstName: "Author",
            name: "Author",
            bio: "",
            role: "author",
          });
        }
      }
    } catch (err) {
      console.error("Error fetching author data:", err);
      setError("Author not found or no posts available");
      toastError("Failed to load author data");
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = blogs.filter(
    (blog) =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.content?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortOptions = [
    { value: "latest", label: "Latest", icon: Sparkles },
    { value: "popular", label: "Most Popular", icon: Flame },
    { value: "trending", label: "Trending", icon: TrendingUp },
  ];

  const handleSort = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-600 dark:text-gray-400">
            Loading author profile...
          </p>
        </div>
      </div>
    );
  }

  if (error || !author) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <User className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Author Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Hero Section with Author Profile */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm">
                  <User className="w-4 h-4 text-primary-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Author Profile
                  </span>
                </div>
              </div>
              <AuthorProfileCard author={author} stats={stats} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Search and Filter Bar */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles by this author..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-3 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-white/70 transition-all"
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm">Filters</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`}
                />
              </button>

              <div className="flex bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 transition-all ${viewMode === "grid" ? "bg-primary-500 text-white" : "text-gray-600 dark:text-gray-400 hover:bg-white/70"}`}
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 transition-all ${viewMode === "list" ? "bg-primary-500 text-white" : "text-gray-600 dark:text-gray-400 hover:bg-white/70"}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                >
                  <div className="flex flex-wrap gap-4 items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sort by:
                    </span>
                    <div className="flex gap-2">
                      {sortOptions.map((option) => {
                        const Icon = option.icon;
                        const isActive = sortBy === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleSort(option.value)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              isActive
                                ? "bg-primary-500 text-white shadow-md"
                                : "bg-white/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-white/70"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {filteredBlogs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                No articles found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {searchTerm
                  ? "No articles match your search criteria."
                  : "This author hasn't published any articles yet."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Clear Search
                </button>
              )}
            </motion.div>
          ) : (
            <>
              <div className="mb-6 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Showing {filteredBlogs.length} of {blogs.length} articles by{" "}
                  {author?.firstName || author?.name || "this author"}
                </p>
              </div>

              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredBlogs.map((blog, index) => (
                    <BlogCard key={blog.id} blog={blog} index={index} />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredBlogs.map((blog, index) => (
                    <BlogListItem key={blog.id} blog={blog} index={index} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && filteredBlogs.length > 0 && (
            <div className="flex justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/60 transition-all"
              >
                Previous
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-all ${
                        currentPage === pageNum
                          ? "bg-primary-500 text-white shadow-md"
                          : "bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 hover:bg-white/60"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/60 transition-all"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogAuthor;
